/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import { StoryCard, storyCardApi, StoryCardsUserBookmarksFeedArgs } from '../services';
import {
  addComment,
  deleteCard,
  deleteComment,
  editComment,
  eventDelete,
  eventUpdateChannels,
  hideComment,
  highlightComment,
  pollVote,
  reactOnCard,
  reactOnComment,
  removeCardFromBookmarks,
  togglePublishOnCard,
  updateCardEvent,
} from './common';
import {
  addCommentReducer,
  deleteCommentReducer,
  editCommentReducer,
  hideCommentReducer,
  highlightCommentReducer,
  pollVoteReducer,
  reactOnCardReducer,
  reactOnCommentReducer,
} from './extraReducers';

interface StoryCardsBookmarksFeedState {
  isFetched: boolean;
  storyCardsBookmarksFeed: StoryCard[];
  page: number;
  hasNextPage: boolean;
}

const initialState: StoryCardsBookmarksFeedState = {
  isFetched: false,
  storyCardsBookmarksFeed: [],
  page: 0,
  hasNextPage: false,
};

const getStoryCardsBookmarksFeedHandler = async (
  args: StoryCardsUserBookmarksFeedArgs,
  { dispatch }: any
) => {
  return dispatch(
    storyCardApi.endpoints.storyCardsUserBookmarksFeed.initiate({ ...args })
  ).unwrap();
};

export const getStoryCardsBookmarksFeed = createAsyncThunk(
  'storyCardsBookmarksFeed',
  getStoryCardsBookmarksFeedHandler
);

export const loadMoreStoryCardsBookmarksFeed = createAsyncThunk(
  'storyCardsBookmarksFeed/loadMore',
  getStoryCardsBookmarksFeedHandler
);

const storyCardsBookmarksFeedSlice = createSlice({
  name: 'storyCardsBookmarksFeed',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getStoryCardsBookmarksFeed.pending, (state) => {
        state.isFetched = false;
      })
      .addCase(getStoryCardsBookmarksFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.isFetched = true;
        state.storyCardsBookmarksFeed = items;
        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(loadMoreStoryCardsBookmarksFeed.fulfilled, (state, action) => {
        const { items, pageInfo } = action.payload;
        const { page, hasNextPage } = pageInfo;

        state.storyCardsBookmarksFeed.push(...items);
        state.page = page;
        state.hasNextPage = hasNextPage;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        const { itemId, comment } = action.payload;

        const card = state.storyCardsBookmarksFeed.find(({ id }) => id === itemId);

        if (!card) {
          return;
        }

        addCommentReducer({ card, comment });
      })
      .addCase(editComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = state.storyCardsBookmarksFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        editCommentReducer({ card, comment });
      })
      .addCase(hideComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = state.storyCardsBookmarksFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        hideCommentReducer({ card, comment });
      })
      .addCase(highlightComment.fulfilled, (state, action) => {
        const { comment } = action.payload;
        const { storyCardId } = comment;

        const card = state.storyCardsBookmarksFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        highlightCommentReducer({ card, comment });
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { deleted, cardId, commentId, parentId, repliesCount } = action.payload;

        if (!deleted) {
          return;
        }

        const card = state.storyCardsBookmarksFeed.find(({ id }) => id === cardId);

        if (!card) {
          return;
        }

        deleteCommentReducer({ card, commentId, parentId, repliesCount });
      })
      .addCase(reactOnComment.fulfilled, (state, action) => {
        const { reactions, cardId, commentId } = action.payload;

        const card = state.storyCardsBookmarksFeed.find(({ id }) => id === cardId);

        if (!card) {
          return;
        }

        reactOnCommentReducer({ card, commentId, reactions });
      })
      .addCase(reactOnCard.fulfilled, (state, action) => {
        const { cardId, reaction, reactions } = action.payload;

        const card = state.storyCardsBookmarksFeed.find(({ id }) => id === cardId);

        if (!card) {
          return;
        }

        reactOnCardReducer({ card, reaction, reactions });
      })
      .addCase(deleteCard.fulfilled, (state, action) => {
        const { deleted, storyCardId } = action.payload;

        if (!deleted) {
          return;
        }

        state.storyCardsBookmarksFeed = state.storyCardsBookmarksFeed.filter(
          ({ id }) => id !== storyCardId
        );
      })
      .addCase(togglePublishOnCard.fulfilled, (state, action) => {
        const { payload } = action.payload;

        if (!payload) {
          return;
        }

        const { id: storyCardId } = payload;

        state.storyCardsBookmarksFeed = state.storyCardsBookmarksFeed.filter(
          ({ id }) => id !== storyCardId
        );
      })
      .addCase(removeCardFromBookmarks.fulfilled, (state, action) => {
        const { payload, storyCardId } = action.payload;

        if (!payload) {
          return;
        }

        state.storyCardsBookmarksFeed = state.storyCardsBookmarksFeed.filter(
          ({ id }) => id !== storyCardId
        );
      })
      .addCase(pollVote.fulfilled, (state, action) => {
        const { payload, storyCardId, selectedVoteIds } = action.payload;

        if (!payload) {
          return;
        }

        const card = state.storyCardsBookmarksFeed.find(({ id }) => id === storyCardId);

        if (!card) {
          return;
        }

        pollVoteReducer({ card, selectedVoteIds });
      })
      .addCase(eventDelete.fulfilled, (state, action) => {
        const { deleted, eventId } = action.payload;

        if (!deleted) {
          return;
        }

        state.storyCardsBookmarksFeed
          .filter((card) => card.eventId === eventId)
          .forEach((card) => {
            card.event = null;
          });
      })
      .addMatcher(
        isAnyOf(updateCardEvent.fulfilled, eventUpdateChannels.fulfilled),
        (state, action) => {
          state.storyCardsBookmarksFeed
            .filter(({ eventId }) => eventId === action.payload.id)
            .forEach((card) => {
              card.event = action.payload;
            });
        }
      );
  },
});

export default storyCardsBookmarksFeedSlice.reducer;
